import { useQuery } from '@tanstack/react-query';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { StorageStand } from '../../../models/Storage/StorageStand';
import { ButtonPrimary } from '../../components/Button/ButtonPrimary';
import { Link, useNavigate } from 'react-router-dom';
import { FaEdit as EditIcon, FaTrash as DeleteIcon, FaFilePdf } from 'react-icons/fa';
import { useState } from 'react';
import axiosAPI from '../../../utils/client/axiosAPI';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../components/Modals/ConfirmationModal/ConfirmationModal';
import style from './storage.module.scss';

type StorageStandListItem = {
  id: number;
  name: string;
  roomName: string;
  productsCount: number;
};

export default function StorageStandList() {
  const {
    data: stands = [],
    refetch,
    isLoading
  } = useQuery(['storage-room-stands'], StorageStand.all);
  const navigate = useNavigate();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<StorageStandListItem | null>(
    null
  );
  const [deleting, setDeleting] = useState(false);

  // Prepare data for table
  const data: StorageStandListItem[] = (stands ?? []).map((stand) => ({
    id: stand.id,
    name: stand.name,
    roomName: stand.storageRoom?.name ?? '',
    productsCount: stand.productsCount ?? 0
  }));

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axiosAPI.delete(`/storage-room-stands/${deleteTarget.id}`);
      toast.success('Polica uspešno obrisana!');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      refetch();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || 'Greška pri brisanju police.'
      );
    } finally {
      setDeleting(false);
    }
  };

  const columns: ColumnsType<StorageStandListItem> = [
    {
      title: 'Naziv',
      dataIndex: 'name',
      key: 'name',
      render: (name, row) => (
        <div
          className="cs-table-link"
          style={{ textAlign: 'left' }}
          onClick={() => navigate(`/admin/storage-stands/edit/${row.id}`)}
        >
          {name}
        </div>
      )
    },
    {
      title: 'Soba',
      dataIndex: 'roomName',
      key: 'roomName',
      render: (roomName) => <span>{roomName}</span>
    },
    {
      title: 'Broj proizvoda',
      dataIndex: 'productsCount',
      key: 'productsCount',
      render: (count) => <span>{count}</span>
    },
    {
      key: 'action',
      title: 'Akcije',
      render: (_, stand) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Link
            className={style.editButton}
            to={`/admin/storage-stands/edit/${stand.id}`}
          >
            <EditIcon />
          </Link>
          <button
            className={style.deleteButton}
            onClick={() => {
              setDeleteTarget(stand);
              setShowDeleteModal(true);
            }}
          >
            <DeleteIcon />
          </button>
          <button
            className={style.pdfButton}
            onClick={() => StorageStand.downloadPdf(stand.id, stand.name)}
            title="Preuzmi PDF sa proizvodima"
          >
            <FaFilePdf style={{ color: '#d32f2f', fontSize: '1.2em' }} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="cs-inner-container">
      <h1 className="cs-title">Lista skladišnih polica</h1>
      <div className={style.info}>
        <div className={style.number}>
          Trenutno imate <strong>{stands.length}</strong> skladišnih polica
        </div>
        <ButtonPrimary url="/admin/storage-stands/create">
          Dodaj Skladišnu Policu
        </ButtonPrimary>
      </div>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={isLoading}
        pagination={false}
        className="cs-table-wrapper"
      />
      <ConfirmationModal
        show={showDeleteModal}
        title="Potvrda brisanja"
        message={
          deleteTarget
            ? `Da li ste sigurni da želite da obrišete policu "${deleteTarget.name}"?`
            : ''
        }
        confirmLabel={deleting ? 'Brišem...' : 'Obriši'}
        cancelLabel="Otkaži"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
}
